export class MoneyParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyParseError';
  }
}

const USD_PATTERN = /^-?\d+(?:\.\d{1,2})?$/;

export function assertMinorUnits(value: number, label = 'amount'): number {
  if (!Number.isInteger(value) || !Number.isSafeInteger(value)) {
    throw new MoneyParseError(`${label} must be an integer number of cents.`);
  }
  return value;
}

export function parseUsdToMinor(value: string, label = 'amount'): number {
  const trimmed = value.trim();
  if (!USD_PATTERN.test(trimmed)) {
    throw new MoneyParseError(`${label} must look like 39.99.`);
  }
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole, fraction = ''] = unsigned.split('.');
  const minor = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return negative ? -minor : minor;
}

export function formatMinorUsd(minor: number): string {
  const safe = assertMinorUnits(minor);
  const sign = safe < 0 ? '-' : '';
  const absolute = Math.abs(safe);
  const dollars = Math.floor(absolute / 100);
  const cents = String(absolute % 100).padStart(2, '0');
  return `${sign}$${dollars}.${cents}`;
}

export function roundHalfUp(numerator: number, denominator: number): number {
  if (!Number.isInteger(numerator) || !Number.isInteger(denominator) || denominator <= 0) {
    throw new MoneyParseError('Rounding requires integer inputs and a positive denominator.');
  }
  const sign = numerator < 0 ? -1 : 1;
  const absolute = Math.abs(numerator);
  const quotient = Math.floor(absolute / denominator);
  const remainder = absolute % denominator;
  return sign * (remainder * 2 >= denominator ? quotient + 1 : quotient);
}

export function percentOfMinor(amountMinor: number, basisPoints: number): number {
  assertMinorUnits(amountMinor, 'amount');
  assertMinorUnits(basisPoints, 'basis points');
  return roundHalfUp(amountMinor * basisPoints, 10_000);
}
